// src/pages/ArtAdvisorWebinar.jsx
// Landing webinaire Art Advisor — MD Lancement Septembre 2026 (§12/§14).
// Angle éditorial : « IA & Art Advisory : ce que l'on peut automatiser, et ce qui
// doit rester humain. » Le produit vient ensuite. Formulaire d'inscription →
// edge function `webinar-register` : upsert crm_leads (dédup email/LinkedIn) +
// crm_activities(webinar_registered) + email de confirmation. UTM capturés depuis
// l'URL et remontés dans le funnel. Aucune dépendance SaaS externe.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { track, getAnonId } from "@/lib/track";
import { BRAND_NAME } from "@/config/brand";
import BrandMark from "@/components/BrandMark";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const AGENDA = [
  "La transformation du métier d'Art Advisor",
  "Les problèmes concrets de données et de documentation",
  "Démonstrations IA — enrichissement, cohérence, valorisation",
  "Les limites de l'IA et le rôle irremplaçable de l'expert",
  `Le workflow ${BRAND_NAME}, de la documentation à la transmission`,
];

const COUNTRIES = ["France", "Belgique", "Suisse", "Luxembourg", "Royaume-Uni", "Autre"];

export default function ArtAdvisorWebinar() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", company: "",
    job_title: "", country: "France", linkedin_url: "", ai_interest: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const formRef = useRef(null);

  // Capture UTM + page d'atterrissage (attribution funnel, MD §13)
  const utm = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || null,
      utm_medium: p.get("utm_medium") || null,
      utm_campaign: p.get("utm_campaign") || null,
      utm_content: p.get("utm_content") || null,
      landing_page: window.location.pathname,
    };
  }, []);

  useEffect(() => {
    track("art_advisor_webinar_view");
    document.title = `Webinaire ${BRAND_NAME} — IA & Art Advisory`;
  }, []);

  const field = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(form.email.trim())) {
      setError("Merci d'indiquer un email professionnel valide.");
      return;
    }
    setSending(true); setError(null);
    try {
      const { data, error: err } = await supabase.functions.invoke("webinar-register", {
        body: {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim().toLowerCase(),
          company: form.company.trim(),
          job_title: form.job_title.trim(),
          country: form.country,
          linkedin_url: form.linkedin_url.trim(),
          ai_interest: form.ai_interest.trim(),
          anon_id: getAnonId(),
          ...utm,
        },
      });
      if (err || !(data && data.ok)) throw err || new Error("register_failed");
      track("art_advisor_webinar_registered", { meta: { country: form.country } });
      setDone(true);
    } catch (err) {
      console.error("[webinar] register:", err);
      setError("Un problème est survenu. Réessayez dans un instant.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full px-3 py-2.5 bg-white border border-[#e5e0d8] rounded-md text-[15px] outline-none focus:border-[#1a1a1a]";

  return (
    <div className="min-h-screen bg-[#f9f7f4] text-[#1a1a1a] font-serif" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-[#f9f7f4]/95 backdrop-blur border-b border-[#e5e0d8]">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark className="text-2xl tracking-[0.15em]" />
          <button
            onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="text-sm px-4 py-2 bg-[#1a1a1a] text-[#f9f7f4] rounded-md hover:bg-black"
          >
            M'inscrire
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-10">
        <p className="text-xs uppercase tracking-[0.22em] text-[#8a8378] mb-4">Webinaire · Art Advisory</p>
        <h1 className="text-3xl md:text-[2.6rem] leading-tight font-bold max-w-3xl">
          IA &amp; Art Advisory : ce que l'on peut réellement automatiser, et ce qui doit rester humain.
        </h1>
        <p className="mt-5 text-[17px] leading-relaxed text-[#4a453d] max-w-2xl">
          Le métier d'Art Advisor évolue. Comment utiliser l'IA, la donnée et l'automatisation
          sans abandonner le jugement, la confidentialité et la relation avec le collectionneur ?
          Une session concrète, sans discours commercial.
        </p>
      </section>

      <div className="max-w-5xl mx-auto px-6 pb-20 grid md:grid-cols-2 gap-10 items-start">
        {/* AGENDA */}
        <div>
          <h2 className="text-sm uppercase tracking-[0.18em] text-[#8a8378] mb-4">Au programme</h2>
          <ul className="space-y-3">
            {AGENDA.map((a, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-1 text-[#c9a961]">◆</span>
                <span className="text-[15px] leading-relaxed text-[#4a453d]">{a}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-[13px] leading-relaxed text-[#8a8378]">
            Programme <strong>Founding Art Advisors</strong> — onboarding accompagné et 30 jours offerts,
            pendant une fenêtre limitée après le webinaire.
          </p>
        </div>

        {/* FORMULAIRE */}
        <div ref={formRef} className="bg-white border border-[#e5e0d8] rounded-2xl p-6 shadow-sm scroll-mt-24">
          {done ? (
            <div className="py-10 text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-[#1a1a1a] text-[#f9f7f4] flex items-center justify-center text-xl">✓</div>
              <h3 className="text-xl font-bold">Inscription enregistrée</h3>
              <p className="mt-2 text-[15px] text-[#4a453d]">
                Vous recevrez la date exacte et le lien de connexion par email avant la session.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <h3 className="text-xl font-bold mb-1">Réserver ma place</h3>
              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Prénom" value={form.first_name} onChange={field("first_name")} />
                <input className={inputCls} placeholder="Nom" value={form.last_name} onChange={field("last_name")} />
              </div>
              <input className={inputCls} type="email" placeholder="Email professionnel *" value={form.email} onChange={field("email")} required />
              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Société / cabinet" value={form.company} onChange={field("company")} />
                <input className={inputCls} placeholder="Fonction" value={form.job_title} onChange={field("job_title")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select className={inputCls} value={form.country} onChange={field("country")}>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input className={inputCls} placeholder="LinkedIn (optionnel)" value={form.linkedin_url} onChange={field("linkedin_url")} />
              </div>
              <textarea
                className={`${inputCls} resize-none`} rows={3}
                placeholder="Quelle utilisation de l'IA vous intéresse le plus ?"
                value={form.ai_interest} onChange={field("ai_interest")}
              />
              {error && <p className="text-sm text-rose-700">{error}</p>}
              <button
                type="submit" disabled={sending}
                className="w-full py-3 bg-[#1a1a1a] text-[#f9f7f4] rounded-md font-semibold hover:bg-black disabled:opacity-50"
              >
                {sending ? "Envoi…" : "M'inscrire au webinaire"}
              </button>
              <p className="text-[12px] leading-relaxed text-[#8a8378]">
                Vos données (nom, email professionnel, société, pays) sont traitées par ARTKIV
                {BRAND_NAME.toUpperCase() !== "ARTKIV" ? `, éditeur de ${BRAND_NAME}` : ""}
                {" "}sur la base de son <strong>intérêt légitime</strong> à organiser ce webinaire et à
                vous présenter son offre Art Advisor. Elles ne sont ni vendues ni cédées à des tiers.
                Vous disposez d'un droit d'accès, de rectification, d'opposition et d'effacement, et
                pouvez vous désinscrire à tout moment. Détails :{" "}
                <Link to="/privacy-policy" className="underline hover:text-[#1a1a1a]">politique de confidentialité</Link>.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
